import { useMemo, useState } from 'react';
import { useMachine } from '@xstate/react';
import { trustMachine } from '../machines/trustMachine';
import { postMutation, fetchState } from '../lib/api';
import { promptById, PROMPTS } from '../lib/prompts';
import { investmentSchema, repairRequestSchema, repairResponseSchema, returnSchema } from '../lib/schemas';
import type { RepairAction, Round } from '../lib/types';
import { useLedgerStore } from '../store';
import { resetTurnstile } from './TurnstileGate';

function money(n?: number | null): string {
  return typeof n === 'number' ? `${n}` : '—';
}

function age(createdAt: string): string {
  const ms = Date.now() - new Date(createdAt).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

export function TrustGame() {
  const [snapshot, send] = useMachine(trustMachine);
  const [promptIndex, setPromptIndex] = useState(() => Math.floor(Math.random() * PROMPTS.length));
  const [claiming, setClaiming] = useState(false);
  const [returnDraft, setReturnDraft] = useState<Record<string, { amount: number; note: string }>>({});
  const [repairDraft, setRepairDraft] = useState<Record<string, { type: 'acknowledge' | 'apology' | 'restitution' | 'second_chance'; note: string }>>({});
  const [repairResponseDraft, setRepairResponseDraft] = useState<Record<string, { type: 'apology' | 'extra_return' | 'explanation' | 'refusal'; note: string }>>({});
  const deviceId = useLedgerStore((store) => store.deviceId);
  const token = useLedgerStore((store) => store.humanToken);
  const state = useLedgerStore((store) => store.state);
  const setState = useLedgerStore((store) => store.setState);
  const setHumanToken = useLedgerStore((store) => store.setHumanToken);
  const setToast = useLedgerStore((store) => store.setToast);
  const prompt = PROMPTS[promptIndex];
  const isArmed = snapshot.matches('armed') || token;
  const sent = snapshot.context.sent;
  const maxReturn = sent * 3;
  const coins = useMemo(() => Array.from({ length: sent }, (_, index) => index), [sent]);

  async function refresh() {
    const next = await fetchState(deviceId);
    setState(next);
  }

  async function commitTrust() {
    const payload = investmentSchema.parse({
      promptId: prompt.id,
      sent,
      predictedReturnPct: snapshot.context.predictedReturnPct,
      investorNote: snapshot.context.note
    });
    if (!token) return setToast('The ledger needs a fresh human pass before it writes.');
    send({ type: 'SUBMIT' });
    const result = await postMutation('invest', deviceId, token, payload);
    setHumanToken('');
    resetTurnstile();
    if (!result.ok) {
      send({ type: 'FAIL' });
      setToast(result.error);
      return;
    }
    send({ type: 'RESOLVE' });
    setPromptIndex((promptIndex + 1) % PROMPTS.length);
    setToast('Your coins crossed. Now the risk belongs to a real stranger.');
    await refresh();
  }

  async function claimRound() {
    if (!token) return setToast('Complete the human pass first.');
    setClaiming(true);
    const result = await postMutation('claim', deviceId, token, {});
    setHumanToken('');
    resetTurnstile();
    setClaiming(false);
    if (!result.ok) setToast(result.error);
    else setToast('A stranger placed trust in your hands.');
    await refresh();
  }

  async function returnTrust(round: Round) {
    const draft = returnDraft[round.id] ?? { amount: Math.ceil(round.maxReturn / 2), note: '' };
    const payload = returnSchema.parse({ roundId: round.id, trusteeReturn: draft.amount, trusteeNote: draft.note });
    if (!token) return setToast('The ledger needs a fresh human pass before it writes.');
    const result = await postMutation('return', deviceId, token, payload);
    setHumanToken('');
    resetTurnstile();
    if (!result.ok) setToast(result.error);
    else setToast(draft.amount >= round.sent ? 'You honored the bridge.' : 'You kept more than they risked. The repair door remains possible.');
    await refresh();
  }

  async function requestRepair(round: Round) {
    const draft = repairDraft[round.id] ?? { type: 'acknowledge' as const, note: '' };
    const payload = repairRequestSchema.parse({ roundId: round.id, requestType: draft.type, requestNote: draft.note });
    if (!token) return setToast('Complete the human pass first.');
    const result = await postMutation('repair-request', deviceId, token, payload);
    setHumanToken('');
    resetTurnstile();
    if (!result.ok) setToast(result.error);
    else setToast('Repair request placed into the ledger.');
    await refresh();
  }

  async function respondRepair(repair: RepairAction) {
    const draft = repairResponseDraft[repair.id] ?? { type: 'apology' as const, note: '' };
    const payload = repairResponseSchema.parse({ repairId: repair.id, responseType: draft.type, responseNote: draft.note });
    if (!token) return setToast('Complete the human pass first.');
    const result = await postMutation('repair-response', deviceId, token, payload);
    setHumanToken('');
    resetTurnstile();
    if (!result.ok) setToast(result.error);
    else setToast('Repair signal returned.');
    await refresh();
  }

  const awaitingReturn = state?.awaitingReturn ?? [];
  const assignments = state?.trusteeAssignments ?? [];
  const awaitingRepair = state?.awaitingRepair ?? [];

  return (
    <div className="grid">
      <section className="panel game-board" aria-labelledby="play-title">
        <div className="card-header">
          <div>
            <h2 id="play-title">{prompt.title}</h2>
            <p className="muted">{prompt.scene}</p>
          </div>
          <span className="badge warm">Trust Game</span>
        </div>

        <div className="bridge" aria-hidden="true">
          <div className="gate you">you</div>
          <div className="gate stranger">?</div>
          {coins.map((coin) => <div className="coin" key={coin} style={{ ['--x' as string]: `${coin * 10}%` }} />)}
        </div>

        <p className="muted"><strong>{prompt.riskQuestion}</strong> Sent coins triple. A stranger can return any amount from 0 to {maxReturn}.</p>

        <div className="slider-row">
          <label htmlFor="sent">Coins to send <span>{sent}/10 → stranger receives {maxReturn}</span></label>
          <input id="sent" type="range" min="0" max="10" value={sent} onChange={(event) => send({ type: 'SET_SENT', sent: Number(event.target.value) })} />
        </div>
        <div className="slider-row">
          <label htmlFor="prediction">Your gut prediction <span>{Math.round(snapshot.context.predictedReturnPct * 100)}% returned</span></label>
          <input id="prediction" type="range" min="0" max="100" value={Math.round(snapshot.context.predictedReturnPct * 100)} onChange={(event) => send({ type: 'SET_PREDICTION', predictedReturnPct: Number(event.target.value) / 100 })} />
        </div>
        <label className="micro" htmlFor="note">Optional pledge / fear / reason. Stored anonymously.</label>
        <textarea id="note" maxLength={280} value={snapshot.context.note} onChange={(event) => send({ type: 'SET_NOTE', note: event.target.value })} placeholder="I’m trusting because… / I’m holding back because…" />
        <div className="actions">
          <button className="primary" disabled={!isArmed || snapshot.matches('committing')} onClick={commitTrust}>Send across the bridge</button>
          <button className="secondary" onClick={() => setPromptIndex((promptIndex + 1) % PROMPTS.length)}>Change scene</button>
        </div>
      </section>

      <aside className="cards">
        <div className="panel">
          <div className="card-header">
            <h3>Hold someone else’s trust</h3>
            <span className="badge">Reciprocity</span>
          </div>
          <p className="muted">Claim an open trust from another anonymous device. The human part is deciding what kind of stranger you become.</p>
          <button className="primary" onClick={claimRound} disabled={claiming || !token}>{claiming ? 'Looking…' : 'Claim a stranger’s trust'}</button>
        </div>

        {assignments.map((round) => {
          const draft = returnDraft[round.id] ?? { amount: Math.ceil(round.maxReturn / 2), note: '' };
          return (
            <div className="card" key={round.id}>
              <div className="card-header">
                <strong>{promptById(round.promptId).title}</strong>
                <span className="badge warm">received {round.sent}→{round.maxReturn}</span>
              </div>
              <p className="muted">A stranger sent {round.sent}; it became {round.maxReturn}. They predicted {Math.round(round.predictedReturnPct * 100)}% would come back.</p>
              {round.investorNote ? <p className="micro">Their note: “{round.investorNote}”</p> : null}
              <div className="slider-row">
                <label>Return amount <span>{draft.amount}/{round.maxReturn}</span></label>
                <input type="range" min="0" max={round.maxReturn} value={draft.amount} onChange={(event) => setReturnDraft({ ...returnDraft, [round.id]: { ...draft, amount: Number(event.target.value) } })} />
              </div>
              <textarea maxLength={280} value={draft.note} onChange={(event) => setReturnDraft({ ...returnDraft, [round.id]: { ...draft, note: event.target.value } })} placeholder="Optional: why this return?" />
              <button className="primary" onClick={() => returnTrust(round)} disabled={!token}>Lock return</button>
            </div>
          );
        })}

        {awaitingReturn.map((round) => (
          <div className="card" key={round.id}>
            <div className="card-header"><strong>Waiting on a stranger</strong><span className="badge">{age(round.createdAt)}</span></div>
            <p className="muted">You sent {round.sent}; it became {round.maxReturn}. This is the felt part: nothing happens until another human touches it.</p>
          </div>
        ))}

        {awaitingRepair.map(({ round, repair }) => {
          const isInvestor = round.investorDevice === deviceId;
          const broke = typeof round.trusteeReturn === 'number' && round.trusteeReturn < round.sent;
          const repairDraftForRound = repairDraft[round.id] ?? { type: 'acknowledge' as const, note: '' };
          const responseDraft = repair ? repairResponseDraft[repair.id] ?? { type: 'apology' as const, note: '' } : { type: 'apology' as const, note: '' };
          return (
            <div className="card" key={`${round.id}-${repair?.id ?? 'none'}`}>
              <div className="card-header">
                <strong>{isInvestor ? 'Outcome arrived' : 'Repair door'}</strong>
                <span className={`badge ${broke ? 'danger' : 'good'}`}>returned {money(round.trusteeReturn)} / {round.maxReturn}</span>
              </div>
              <p className="muted">You sent {round.sent}; the stranger returned {money(round.trusteeReturn)}. {broke ? 'That is a breach by the game’s plain standard: they gave back less than you risked.' : 'This crossed the bridge intact.'}</p>

              {isInvestor && broke && !repair ? (
                <>
                  <select value={repairDraftForRound.type} onChange={(event) => setRepairDraft({ ...repairDraft, [round.id]: { ...repairDraftForRound, type: event.target.value as typeof repairDraftForRound.type } })}>
                    <option value="acknowledge">Ask them to acknowledge the breach</option>
                    <option value="apology">Ask for apology</option>
                    <option value="restitution">Ask for restitution</option>
                    <option value="second_chance">Offer a second-chance frame</option>
                  </select>
                  <textarea maxLength={280} value={repairDraftForRound.note} onChange={(event) => setRepairDraft({ ...repairDraft, [round.id]: { ...repairDraftForRound, note: event.target.value } })} placeholder="Name the breach without naming yourself." />
                  <button className="danger-soft" disabled={!token} onClick={() => requestRepair(round)}>Open repair door</button>
                </>
              ) : null}

              {!isInvestor && repair && !repair.respondedAt ? (
                <>
                  <p className="micro">Repair request: {repair.requestType}. “{repair.requestNote || 'No note.'}”</p>
                  <select value={responseDraft.type} onChange={(event) => setRepairResponseDraft({ ...repairResponseDraft, [repair.id]: { ...responseDraft, type: event.target.value as typeof responseDraft.type } })}>
                    <option value="apology">Apologize</option>
                    <option value="extra_return">Offer extra return next time</option>
                    <option value="explanation">Explain</option>
                    <option value="refusal">Refuse</option>
                  </select>
                  <textarea maxLength={280} value={responseDraft.note} onChange={(event) => setRepairResponseDraft({ ...repairResponseDraft, [repair.id]: { ...responseDraft, note: event.target.value } })} placeholder="A repair signal, if any." />
                  <button className="primary" disabled={!token} onClick={() => respondRepair(repair)}>Send repair signal</button>
                </>
              ) : null}

              {repair?.respondedAt ? <p className="micro">Repair response: {repair.responseType}. “{repair.responseNote || 'No note.'}”</p> : null}
            </div>
          );
        })}
      </aside>
    </div>
  );
}
